import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { ArrowDown, ArrowUp, ChevronLeft, Mail, Plus, RotateCcw, Send, Trash2 } from 'lucide-react';
import {
  emailTemplatesAPI,
  type EmailBlock,
  type EmailContent,
  type EmailTemplateDetail,
  type EmailTemplateSummary,
} from '../api/client';

type EditableBlock = EmailBlock & { _id: number };

const BLOCK_LABELS: Record<EmailBlock['type'], string> = {
  paragraph: 'Paragraph',
  note: 'Small gray note',
  callout: 'Highlight box',
  list: 'Numbered or bulleted list',
  button: 'Button',
  personal_note: 'Inviter’s personal note',
  body: 'Broadcast message',
};

const NEW_BLOCKS: Record<'paragraph' | 'note' | 'callout' | 'list' | 'button', () => EmailBlock> = {
  paragraph: () => ({ type: 'paragraph', text: '' }),
  note: () => ({ type: 'note', text: '' }),
  callout: () => ({ type: 'callout', text: '', tone: 'info' }),
  list: () => ({ type: 'list', items: [''], ordered: true }),
  button: () => ({ type: 'button', label: '', link: '' }),
};

let nextId = 1;
const withIds = (blocks: EmailBlock[]): EditableBlock[] => blocks.map((b) => ({ ...b, _id: nextId++ }));
const stripIds = (blocks: EditableBlock[]): EmailBlock[] =>
  blocks.map((b) => {
    const copy: Partial<EditableBlock> = { ...b };
    delete copy._id;
    return copy as EmailBlock;
  });

function errorText(err: unknown, fallback: string) {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as { error?: string } | undefined;
    if (data?.error) return data.error;
  }
  return fallback;
}

function formatDate(value: string) {
  return new Date(value).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

export default function EmailTemplatesAdmin() {
  const [templates, setTemplates] = useState<EmailTemplateSummary[]>([]);
  const [detail, setDetail] = useState<EmailTemplateDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const loadList = useCallback(async () => {
    try {
      setTemplates(await emailTemplatesAPI.list());
    } catch {
      toast.error('Couldn’t load emails');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadList();
  }, [loadList]);

  const open = async (key: string) => {
    try {
      setDetail(await emailTemplatesAPI.get(key));
      window.scrollTo(0, 0);
    } catch {
      toast.error('Couldn’t load that email');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-navy" />
      </div>
    );
  }

  if (detail) {
    return (
      <EmailEditor
        key={`${detail.key}-${detail.versions.find((v) => v.is_active)?.id ?? 'default'}`}
        detail={detail}
        onBack={() => {
          setDetail(null);
          loadList();
        }}
        onReload={() => open(detail.key)}
      />
    );
  }

  const groups = Array.from(new Set(templates.map((t) => t.group)));
  return (
    <div className="max-w-5xl mx-auto px-4 py-6">
      <h1 className="text-2xl font-bold text-navy mb-1">Emails</h1>
      <p className="text-gray-600 mb-6">
        Edit the wording of every email the app sends. Changes apply to the next email that goes out.
      </p>
      {groups.map((group) => (
        <section key={group} className="mb-8">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500 mb-3">{group}</h2>
          <div className="grid gap-3 sm:grid-cols-2">
            {templates.filter((t) => t.group === group).map((t) => (
              <button
                key={t.key}
                onClick={() => open(t.key)}
                className="text-left bg-white rounded-xl border border-gray-200 p-4 hover:border-navy transition"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-semibold text-navy">{t.name}</span>
                  {t.customized && (
                    <span className="text-xs font-medium bg-gold/15 text-amber-800 rounded-full px-2 py-0.5">Edited</span>
                  )}
                </div>
                <p className="text-sm text-gray-600 mt-1">{t.description}</p>
                <p className="text-xs text-gray-500 mt-2 truncate">Subject: {t.subject}</p>
              </button>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

function EmailEditor({
  detail,
  onBack,
  onReload,
}: {
  detail: EmailTemplateDetail;
  onBack: () => void;
  onReload: () => void;
}) {
  const [subject, setSubject] = useState(detail.content.subject);
  const [heading, setHeading] = useState(detail.content.heading);
  const [greeting, setGreeting] = useState(detail.content.greeting);
  const [blocks, setBlocks] = useState<EditableBlock[]>(() => withIds(detail.content.blocks));
  const [notes, setNotes] = useState('');
  const [preview, setPreview] = useState<{ subject: string; html: string } | null>(null);
  const [previewError, setPreviewError] = useState('');
  const [busy, setBusy] = useState<'' | 'save' | 'test' | 'reset' | number>('');
  const [addOpen, setAddOpen] = useState(false);

  const content: EmailContent = useMemo(
    () => ({ subject, heading, greeting, blocks: stripIds(blocks) }),
    [subject, heading, greeting, blocks],
  );
  const dirty = JSON.stringify(content) !== JSON.stringify(detail.content);

  const previewTimer = useRef<number | undefined>(undefined);
  useEffect(() => {
    window.clearTimeout(previewTimer.current);
    previewTimer.current = window.setTimeout(async () => {
      try {
        const result = await emailTemplatesAPI.preview(detail.key, content);
        setPreview(result);
        setPreviewError('');
      } catch (err) {
        setPreviewError(errorText(err, 'Preview unavailable'));
      }
    }, 400);
    return () => window.clearTimeout(previewTimer.current);
  }, [content, detail.key]);

  const update = (id: number, patch: Partial<EditableBlock>) =>
    setBlocks((prev) => prev.map((b) => (b._id === id ? ({ ...b, ...patch } as EditableBlock) : b)));
  const move = (index: number, delta: number) =>
    setBlocks((prev) => {
      const next = [...prev];
      const [item] = next.splice(index, 1);
      next.splice(index + delta, 0, item);
      return next;
    });
  const remove = (id: number) => setBlocks((prev) => prev.filter((b) => b._id !== id));
  const add = (kind: keyof typeof NEW_BLOCKS) => {
    setBlocks((prev) => [...prev, ...withIds([NEW_BLOCKS[kind]()])]);
    setAddOpen(false);
  };

  const save = async () => {
    setBusy('save');
    try {
      await emailTemplatesAPI.save(detail.key, content, notes);
      toast.success('Saved. New emails will use this version.');
      onReload();
    } catch (err) {
      toast.error(errorText(err, 'Couldn’t save'));
    } finally {
      setBusy('');
    }
  };

  const sendTest = async () => {
    setBusy('test');
    try {
      const { sent_to } = await emailTemplatesAPI.sendTest(detail.key, content);
      toast.success(`Test sent to ${sent_to}`);
    } catch (err) {
      toast.error(errorText(err, 'Couldn’t send the test'));
    } finally {
      setBusy('');
    }
  };

  const reset = async () => {
    if (!window.confirm('Go back to the original built-in wording? Your saved versions stay in the history.')) return;
    setBusy('reset');
    try {
      await emailTemplatesAPI.reset(detail.key);
      toast.success('Back to the original wording');
      onReload();
    } catch (err) {
      toast.error(errorText(err, 'Couldn’t reset'));
    } finally {
      setBusy('');
    }
  };

  const restore = async (versionId: number, version: number) => {
    if (!window.confirm(`Start sending version ${version} again?`)) return;
    setBusy(versionId);
    try {
      await emailTemplatesAPI.restore(detail.key, versionId);
      toast.success(`Version ${version} is now in use`);
      onReload();
    } catch (err) {
      toast.error(errorText(err, 'Couldn’t restore'));
    } finally {
      setBusy('');
    }
  };

  const loadDefault = () => {
    setSubject(detail.default_content.subject);
    setHeading(detail.default_content.heading);
    setGreeting(detail.default_content.greeting);
    setBlocks(withIds(detail.default_content.blocks));
  };

  const inputClass = 'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-navy focus:outline-none';

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <button onClick={onBack} className="inline-flex items-center gap-1 text-sm text-navy mb-3">
        <ChevronLeft size={16} /> All emails
      </button>
      <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
        <div>
          <h1 className="text-2xl font-bold text-navy">{detail.name}</h1>
          <p className="text-gray-600 text-sm">{detail.description}</p>
        </div>
        <span className="text-xs text-gray-500">
          {detail.customized ? 'Using an edited version' : 'Using the original wording'}
        </span>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
            <label className="block">
              <span className="text-sm font-medium text-gray-700">Subject line</span>
              <input className={inputClass} value={subject} onChange={(e) => setSubject(e.target.value)} />
            </label>
            <label className="block">
              <span className="text-sm font-medium text-gray-700">Heading</span>
              <input className={inputClass} value={heading} onChange={(e) => setHeading(e.target.value)} />
            </label>
            <label className="block">
              <span className="text-sm font-medium text-gray-700">Greeting</span>
              <input className={inputClass} value={greeting} onChange={(e) => setGreeting(e.target.value)} />
            </label>
          </div>

          <div className="bg-navy/5 rounded-xl p-4 text-sm text-gray-700 space-y-2">
            <p>
              <span className="font-medium">Fill-ins</span> (replaced for each person):{' '}
              {detail.variables.map((v) => (
                <code key={v.name} title={v.description} className="mr-1.5 inline-block rounded bg-white px-1.5 py-0.5 text-xs">
                  {`{${v.name}}`}
                </code>
              ))}
            </p>
            <p>
              <span className="font-medium">Formatting:</span> <code className="text-xs">**bold**</code> and links like{' '}
              <code className="text-xs">[click here]({detail.links[0]?.name ?? 'https://…'})</code>. A link can be any of
              this email’s links ({detail.links.map((l) => l.name).join(', ') || 'none'}) or a full https:// address.
            </p>
          </div>

          {blocks.map((block, index) => (
            <div key={block._id} className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">{BLOCK_LABELS[block.type]}</span>
                <div className="flex items-center gap-1 text-gray-500">
                  <button aria-label="Move up" disabled={index === 0} onClick={() => move(index, -1)} className="p-1 disabled:opacity-30 hover:text-navy">
                    <ArrowUp size={16} />
                  </button>
                  <button aria-label="Move down" disabled={index === blocks.length - 1} onClick={() => move(index, 1)} className="p-1 disabled:opacity-30 hover:text-navy">
                    <ArrowDown size={16} />
                  </button>
                  <button aria-label="Remove" onClick={() => remove(block._id)} className="p-1 hover:text-red-600">
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
              <BlockFields block={block} links={detail.links} onChange={(patch) => update(block._id, patch)} inputClass={inputClass} />
            </div>
          ))}

          <div className="relative">
            <button
              onClick={() => setAddOpen((o) => !o)}
              className="inline-flex items-center gap-1.5 rounded-lg border border-dashed border-gray-400 px-3 py-2 text-sm text-gray-700 hover:border-navy hover:text-navy"
            >
              <Plus size={16} /> Add a block
            </button>
            {addOpen && (
              <div className="absolute z-10 mt-1 w-56 rounded-lg border border-gray-200 bg-white shadow-lg">
                {(Object.keys(NEW_BLOCKS) as (keyof typeof NEW_BLOCKS)[]).map((kind) => (
                  <button key={kind} onClick={() => add(kind)} className="block w-full px-3 py-2 text-left text-sm hover:bg-gray-50">
                    {BLOCK_LABELS[kind]}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
            <label className="block">
              <span className="text-sm font-medium text-gray-700">What changed? (optional, shows in history)</span>
              <input className={inputClass} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. Added paragraph before step 3" />
            </label>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={save}
                disabled={!dirty || busy !== ''}
                className="rounded-lg bg-navy px-4 py-2 text-sm font-semibold text-white disabled:opacity-40"
              >
                {busy === 'save' ? 'Saving…' : 'Save and use this version'}
              </button>
              <button
                onClick={sendTest}
                disabled={busy !== ''}
                className="inline-flex items-center gap-1.5 rounded-lg border border-navy px-4 py-2 text-sm font-semibold text-navy disabled:opacity-40"
              >
                <Send size={14} /> {busy === 'test' ? 'Sending…' : 'Send test to me'}
              </button>
              {dirty && (
                <button
                  onClick={() => {
                    setSubject(detail.content.subject);
                    setHeading(detail.content.heading);
                    setGreeting(detail.content.greeting);
                    setBlocks(withIds(detail.content.blocks));
                  }}
                  className="rounded-lg px-3 py-2 text-sm text-gray-600 hover:text-navy"
                >
                  Discard changes
                </button>
              )}
            </div>
            <p className="text-xs text-gray-500">The test uses sample details (sample links and names) and goes to your own email.</p>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <h2 className="font-semibold text-navy mb-2">History</h2>
            <ul className="divide-y divide-gray-100 text-sm">
              {detail.versions.map((v) => (
                <li key={v.id} className="flex items-center justify-between gap-3 py-2">
                  <div>
                    <span className="font-medium">Version {v.version}</span>
                    {v.is_active && <span className="ml-2 text-xs text-green-700">In use</span>}
                    <div className="text-xs text-gray-500">
                      {formatDate(v.created_at)}{v.created_by ? ` · ${v.created_by}` : ''}{v.notes ? ` · ${v.notes}` : ''}
                    </div>
                  </div>
                  {!v.is_active && (
                    <button onClick={() => restore(v.id, v.version)} disabled={busy !== ''} className="text-xs font-medium text-navy hover:underline">
                      {busy === v.id ? 'Restoring…' : 'Use this version'}
                    </button>
                  )}
                </li>
              ))}
              <li className="flex items-center justify-between gap-3 py-2">
                <div>
                  <span className="font-medium">Original wording</span>
                  {!detail.customized && <span className="ml-2 text-xs text-green-700">In use</span>}
                </div>
                <div className="flex gap-3">
                  <button onClick={loadDefault} className="text-xs font-medium text-navy hover:underline">Load into editor</button>
                  {detail.customized && (
                    <button onClick={reset} disabled={busy !== ''} className="inline-flex items-center gap-1 text-xs font-medium text-navy hover:underline">
                      <RotateCcw size={12} /> {busy === 'reset' ? 'Resetting…' : 'Use original'}
                    </button>
                  )}
                </div>
              </li>
            </ul>
          </div>
        </div>

        <div className="lg:sticky lg:top-4 self-start">
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="flex items-center gap-2 border-b border-gray-200 px-4 py-2 text-sm">
              <Mail size={16} className="text-gray-500" />
              <span className="truncate font-medium">{preview?.subject ?? subject}</span>
            </div>
            {previewError && <p className="px-4 py-2 text-sm text-red-700 bg-red-50">{previewError}</p>}
            <iframe
              title="Email preview"
              srcDoc={preview?.html ?? ''}
              sandbox=""
              className="w-full h-[75vh] bg-[#f5f2eb]"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function BlockFields({
  block,
  links,
  onChange,
  inputClass,
}: {
  block: EditableBlock;
  links: EmailTemplateDetail['links'];
  onChange: (patch: Partial<EditableBlock>) => void;
  inputClass: string;
}) {
  switch (block.type) {
    case 'paragraph':
    case 'note':
      return <textarea className={inputClass} rows={3} value={block.text} onChange={(e) => onChange({ text: e.target.value })} />;
    case 'callout':
      return (
        <div className="space-y-2">
          <textarea className={inputClass} rows={3} value={block.text} onChange={(e) => onChange({ text: e.target.value })} />
          <select className={inputClass} value={block.tone} onChange={(e) => onChange({ tone: e.target.value as 'info' | 'warning' | 'success' })}>
            <option value="info">Gold (information)</option>
            <option value="warning">Red (warning)</option>
            <option value="success">Green (good news)</option>
          </select>
        </div>
      );
    case 'list':
      return (
        <div className="space-y-2">
          <textarea
            className={inputClass}
            rows={4}
            value={block.items.join('\n')}
            onChange={(e) => onChange({ items: e.target.value.split('\n') })}
            placeholder="One item per line"
          />
          <label className="inline-flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={block.ordered} onChange={(e) => onChange({ ordered: e.target.checked })} />
            Numbered (unchecked = bullets)
          </label>
        </div>
      );
    case 'button': {
      const known = links.some((l) => l.name === block.link);
      const custom = !known && block.link !== '';
      return (
        <div className="space-y-2">
          <input className={inputClass} value={block.label} onChange={(e) => onChange({ label: e.target.value })} placeholder="Button text" />
          <select
            className={inputClass}
            value={known ? block.link : custom ? '__custom' : ''}
            onChange={(e) => onChange({ link: e.target.value === '__custom' ? 'https://' : e.target.value })}
          >
            <option value="">Choose where it goes…</option>
            {links.map((l) => (
              <option key={l.name} value={l.name}>{l.label}</option>
            ))}
            <option value="__custom">Another web address…</option>
          </select>
          {custom && (
            <input className={inputClass} value={block.link} onChange={(e) => onChange({ link: e.target.value })} placeholder="https://" />
          )}
        </div>
      );
    }
    case 'personal_note':
      return <p className="text-sm text-gray-600">If the inviter typed a personal note, it appears here. Move or remove this block to change that.</p>;
    case 'body':
      return <p className="text-sm text-gray-600">The message typed on the Broadcast page appears here.</p>;
  }
}
